import React from "react"
import styled, { css } from "styled-components"
import colors from "@constants/colors"
import breakpoints from "@constants/breakpoints"
import { smallcapsBold13Styles } from "@constants/typography"
import { Arrow } from "@components/icons"
import Scrollbar, { ScrollButtons } from "@components/Scrollbar"
import useResizeHandler from "@hooks/useResizeHandler"
import TweenLite, { Power4 } from "gsap/TweenLite"
import _ from "lodash-es"

const Container = styled.div`
  margin: 64px 0 90px;
  padding-left: 20px;

  @media ${breakpoints.tablet} {
    margin-bottom: 184px;
    padding-left: 0;
  }

  @media ${breakpoints.laptop} {
    display: flex;
    margin-bottom: 168px;
    padding-left: 0;
  }
`

const ContentWrapper = styled.div`
  @media ${breakpoints.tablet} {
    padding-left: calc(1 / 12 * 100vw);
  }

  @media ${breakpoints.laptop} {
    width: calc(11 / 12 * 100vw);
    padding-left: 0;
  }
`

const Content = styled.div<{ dynamicHeight?: boolean }>`
  ${({ dynamicHeight }) => css`
    display: ${dynamicHeight ? "flex" : "block"};
    white-space: nowrap;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    -ms-overflow-style: none;
    scrollbar-width: none;
    ::-webkit-scrollbar {
      display: none;
    }
  `}
`

const Controls = styled.div`
  display: none;

  @media ${breakpoints.laptop} {
    flex-direction: column;
    justify-content: space-between;
    display: flex;
    width: calc(1 / 12 * 100vw);
    padding-bottom: 88px;
  }
`

const ArrowContainer = styled.div`
  flex-direction: column;
  justify-content: space-between;
  display: flex;
  width: 100%;
  height: 120px;
`

const ArrowButton = styled.button`
  align-items: center;
  justify-content: center;
  display: flex;
  height: 50px;
  padding: 0;
  border: none;
  outline: none;
  background-color: transparent;
  cursor: pointer;
  width: 50px;
  margin: 0 auto;

  &:hover {
    background-color: ${colors.dividerLineGray};
  }

  &:active {
    background-color: ${colors.backgroundGrayHover};
  }
`

const LeftArrow = styled.div`
  transform: rotate(180deg);
  width: 20px;
  height: 20px;
`

const RightArrow = styled.div`
  width: 20px;
  height: 20px;
`

const Counter = styled.div`
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  display: flex;
  width: 100%;
  height: 96px;
`

const CounterText = styled.div`
  ${smallcapsBold13Styles}
`

const CounterDivider = styled(CounterText)`
  transform: translateY(-6px);
`

const Scroll = styled(Scrollbar)`
  padding-top: 20px;
  padding-right: 20px;

  @media ${breakpoints.laptop} {
    padding-top: 80px;
    padding-right: 184px;
  }

  ${ScrollButtons} {
    @media ${breakpoints.laptop} {
      display: none;
    }
  }
`

interface SliderProps {
  blockId?: string
  slideCount: number
  children: React.ReactNode
  dynamicHeight?: boolean
}

const Slider: React.FC<SliderProps> = ({
  blockId,
  slideCount,
  children,
  dynamicHeight,
}) => {
  const controlRef = React.createRef<HTMLDivElement>()
  const contentRef = React.createRef<HTMLDivElement>()
  const [columnWidth, setColumnWidth] = React.useState<number>(0)
  const [count, setCount] = React.useState(1)

  const updateColumnWidth = () => {
    if (controlRef && controlRef.current) {
      setColumnWidth(
        Math.floor(controlRef.current.getBoundingClientRect().width)
      )
    }
  }

  React.useEffect(() => {
    updateColumnWidth()
  }, [controlRef])

  useResizeHandler({ onResize: updateColumnWidth, runForProps: [controlRef] })

  const calculateNumber = () => {
    const container = contentRef.current
    if (container) {
      if (
        container.scrollWidth - (container.clientWidth + columnWidth) <
        container.scrollLeft
      ) {
        setCount(slideCount)
      } else {
        setCount(
          Math.floor(
            (container.scrollLeft + columnWidth / 2) /
              (container.scrollWidth / slideCount)
          ) + 1
        )
      }
    }
  }

  React.useEffect(() => {
    if (contentRef.current) {
      contentRef.current.addEventListener("scroll", calculateNumber)
    }
    return () => {
      if (contentRef.current) {
        contentRef.current.removeEventListener("scroll", calculateNumber)
      }
    }
  }, [contentRef])

  const onPrev = () => {
    const container = contentRef.current
    if (container) {
      const left = _.clamp(
        container.scrollLeft - (container.clientWidth - columnWidth / 2),
        0,
        container.scrollWidth - container.clientWidth
      )
      TweenLite.to(container, 0.4, { scrollLeft: left, ease: Power4.easeOut })
    }
  }

  const onNext = () => {
    const container = contentRef.current
    if (container) {
      const left = _.clamp(
        container.scrollLeft + (container.clientWidth - columnWidth / 2),
        0,
        container.scrollWidth - container.clientWidth
      )
      TweenLite.to(container, 0.4, { scrollLeft: left, ease: Power4.easeOut })
    }
  }

  return (
    <Container id={blockId || undefined}>
      <Controls ref={controlRef}>
        <ArrowContainer>
          <ArrowButton onClick={onNext}>
            <RightArrow>
              <Arrow strokeColor={colors.black} strokeWidth="1.5px" />
            </RightArrow>
          </ArrowButton>
          <ArrowButton onClick={onPrev}>
            <LeftArrow>
              <Arrow strokeColor={colors.black} strokeWidth="1.5px" />
            </LeftArrow>
          </ArrowButton>
        </ArrowContainer>
        <Counter>
          <CounterText>{count}</CounterText>
          <CounterDivider>_</CounterDivider>
          <CounterText>{slideCount}</CounterText>
        </Counter>
      </Controls>
      <ContentWrapper>
        <Content ref={contentRef} dynamicHeight={dynamicHeight}>
          {children}
        </Content>
        <Scroll containerRef={contentRef} />
      </ContentWrapper>
    </Container>
  )
}

export default Slider
